// Layer (audit §4b): what sits above the page, drawn one way at three heights.
// - pop: a pop-over, a menu, a hover card. Popover puts it under its button.
// - panel: the side panel. A veil lightens what's behind it; clicks pass through the veil to the board.
// - dialog: a modal. A scrim behind it, focus kept inside, Escape and Close dismiss it.
// Every layer: a frame (--layer-frame) and a hard-edged cast with no blur, further out the higher it sits
// (DESIGN.md §1, Amir 25 Oct), and Close in its top-right corner. Its first line is the thing itself,
// never a heading that names what was clicked: that name is the layer's label, for screen readers.
// Reads only --layer-*, --sheet-* and --dialog-*.
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { Close } from '../Close/Close.tsx';
import './Layer.css';

export type LayerLevel = 'pop' | 'panel' | 'dialog';
/** A dialog's width (--dialog-w-*): sm a short decision, md a form, lg a wide form, fill the photo viewer. */
export type DialogSize = 'sm' | 'md' | 'lg' | 'fill';

interface FrameProps {
  level: LayerLevel;
  /** Its name for screen readers ("The 10:15 visit"), never drawn as a heading. */
  label: string;
  children: ReactNode;
  /** Draws Close in the corner; Escape does the same. */
  onClose?: () => void;
  /**
   * A pop layer that keeps to the room its pop-over is given (a menu): the frame stops at it and the body
   * scrolls inside, so the cast stays drawn.
   */
  fit?: boolean;
}

/**
 * Close's words for a layer: "Close" and its label, kept as written. Only a sentence's first word
 * drops its capital ("The filter" → "Close the filter", "New estimate" → "Close new estimate"); a
 * name keeps it ("Sam Rivera · Tue Oct 27" → "Close Sam Rivera · Tue Oct 27"): two capitalised words
 * in a row, a word with a capital inside it ("iPad"), or a possessive ("Sam’s day").
 */
export function closeLabel(label: string): string {
  const [first = '', second = ''] = label.split(/\s+/);
  const keep =
    !/^\p{Lu}/u.test(first) ||
    /\p{Lu}/u.test(first.slice(1)) ||
    /['’]s$/.test(first) ||
    /^\p{Lu}\p{Ll}/u.test(second);
  return `Close ${keep ? label : `${first.charAt(0).toLowerCase()}${label.slice(1)}`}`;
}

/**
 * Escape goes to the topmost layer only. A native modal <dialog> opened inside a layer (the shared photo
 * viewer, with showModal) closes itself on Escape; the same keydown then bubbles out to the layer under
 * it, which must leave it alone (QA-778). True when the key was pressed in an open <dialog> that the
 * layer `own` doesn't sit inside: a layer above this one.
 */
export function escapeIsAbove(target: EventTarget | null, own: Node | null): boolean {
  const el = target as Partial<Element> | null;
  const inner = typeof el?.closest === 'function' ? el.closest('dialog[open]') : null;
  return !!inner && !(own && inner.contains(own));
}

/** The drawing alone: a frame, its cast, Close in the corner. Popover and the dialog wrap it. */
export function LayerFrame({
  level,
  label,
  children,
  onClose,
  fit,
  onKeyDown,
}: FrameProps & { onKeyDown?: (e: ReactKeyboardEvent<HTMLDivElement>) => void }) {
  return (
    <div
      className="fl-layer"
      data-level={level}
      data-closable={onClose ? '' : undefined}
      data-fit={fit ? '' : undefined}
      onKeyDown={onKeyDown}
    >
      {onClose ? <Close onPress={onClose} label={closeLabel(label)} /> : null}
      <div className="fl-layer__body">{children}</div>
    </div>
  );
}

export type LayerProps = FrameProps & {
  /** For a panel or a dialog: whether it shows. A pop layer is shown by its Popover. */
  isOpen?: boolean;
  /** A dialog's width; md (--dialog-w) unless it says. */
  size?: DialogSize;
  /** A dialog: whether a click on the scrim closes it (true). A form with unsaved words says false. */
  isDismissable?: boolean;
  /** A panel's width: --sheet-w, or wide (--sheet-w-wide) for a record's detail with tables. */
  width?: 'default' | 'wide';
  /**
   * A panel opened from a row (round 2: a visit's or a history entry's detail): the focus moves into it
   * when it opens and goes back to what opened it when it closes.
   */
  takesFocus?: boolean;
};

export function Layer({
  level,
  isOpen = true,
  size = 'md',
  isDismissable = true,
  width,
  takesFocus,
  ...frame
}: LayerProps) {
  if (level === 'pop') return <LayerFrame level="pop" {...frame} />;
  if (level === 'dialog')
    return <DialogLayer isOpen={isOpen} size={size} isDismissable={isDismissable} {...frame} />;
  return isOpen ? <PanelLayer width={width} takesFocus={takesFocus} {...frame} /> : null;
}

function PanelLayer({
  label,
  children,
  onClose,
  width,
  takesFocus,
}: Omit<FrameProps, 'level'> & Pick<LayerProps, 'width' | 'takesFocus'>) {
  const own = useRef<HTMLElement>(null);
  // opened from a row: the focus goes in, and back to the row when it closes
  useEffect(() => {
    if (!takesFocus) return;
    const from = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    own.current?.focus({ preventScroll: true });
    return () => {
      if (from?.isConnected) from.focus({ preventScroll: true });
    };
  }, [takesFocus]);
  // A panel that takes the focus keeps it: Tab past its last control comes back to its first (the page
  // behind is veiled), until Escape or Close gives it back to the row it came from.
  useEffect(() => {
    if (!takesFocus) return;
    const el = own.current;
    if (!el) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const all = [
        ...el.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((x) => x.offsetParent !== null);
      if (!all.length) return;
      const first = all[0]!;
      const last = all[all.length - 1]!;
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && at === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el.addEventListener('keydown', onTab);
    return () => el.removeEventListener('keydown', onTab);
  }, [takesFocus]);
  // Escape closes the panel from anywhere on the page, as its Close does, unless a layer above it had it.
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !escapeIsAbove(e.target, own.current)) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="fl-layer-veil" aria-hidden="true" />
      <section
        ref={own}
        className="fl-layer-panel"
        role="dialog"
        aria-modal="false"
        aria-label={label}
        data-width={width === 'wide' ? 'wide' : undefined}
        tabIndex={takesFocus ? -1 : undefined}
      >
        <LayerFrame level="panel" label={label} onClose={onClose}>
          {children}
        </LayerFrame>
      </section>
    </>
  );
}

/**
 * The dialog's own Escape (react-aria's, on the modal around the frame) runs as the key bubbles out of
 * it: a key pressed in a native <dialog> above stops here, so only that one closes and its focus goes
 * back to what opened it.
 */
function keepEscapeAbove(e: ReactKeyboardEvent<HTMLDivElement>) {
  if (e.key === 'Escape' && escapeIsAbove(e.target, e.currentTarget)) e.stopPropagation();
}

function DialogLayer({
  label,
  children,
  onClose,
  isOpen,
  size,
  isDismissable,
}: Omit<FrameProps, 'level'> & { isOpen: boolean; size: DialogSize; isDismissable: boolean }) {
  return (
    <ModalOverlay
      className="fl-layer-scrim"
      isOpen={isOpen}
      isDismissable={isDismissable}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
    >
      <Modal className="fl-layer-modal" data-size={size}>
        <Dialog className="fl-layer-dialog" aria-label={label}>
          {({ close }) => (
            <LayerFrame level="dialog" label={label} onClose={close} onKeyDown={keepEscapeAbove}>
              {children}
            </LayerFrame>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
